import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import axios from "axios";
import Swal from "sweetalert2";

import ActionsMenu from "./ActionsMenu";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const initialForm = {
  role_code: "",
  role_name: "",
  role_description: "",
  is_system_role: 0,
  is_active: 1,
};

const Roles = () => {
  const [rows, setRows] = useState([]);

  const [form, setForm] = useState(initialForm);

  const [openDialog, setOpenDialog] = useState(false);
  const [editId, setEditId] = useState(null);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = user.client_id;
  const collegeId = user.college_id;

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // ---------------------------------------------------------
  // LOAD
  // ---------------------------------------------------------

  const loadRoles = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/roles/${clientId}/${collegeId}`,
        {
          params: {
            page: page + 1,
            pageSize,
            search,
          },
        }
      );

      const result = response.data;

      const items = result?.data?.items || result?.items || result?.rows || [];

      const total =
        result?.data?.total ?? result?.total ?? result?.totalCount ?? result?.rowCount ?? 0;

      setRows(
        Array.isArray(items)
          ? items.map((item) => ({
              ...item,
              id: item.role_id,
            }))
          : []
      );

      setRowCount(Number(total));
    } catch (error) {
      console.error(error);

      showSnackbar(error.response?.data?.message || "Failed to load roles", "error");

      setRows([]);

      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId) {
      loadRoles();
    }
  }, [clientId, collegeId, page, pageSize, search]);

  // ---------------------------------------------------------
  // ADD
  // ---------------------------------------------------------

  const handleAdd = () => {
    setEditId(null);

    setForm({
      ...initialForm,
    });

    setOpenDialog(true);
  };

  // ---------------------------------------------------------
  // EDIT
  // ---------------------------------------------------------

  const handleEdit = (row) => {
    setEditId(row.role_id);

    setForm({
      role_code: row.role_code || "",
      role_name: row.role_name || "",
      role_description: row.role_description || "",
      is_system_role: row.is_system_role ? 1 : 0,
      is_active: row.is_active ? 1 : 0,
    });

    setOpenDialog(true);
  };

  // ---------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------

  const handleSave = async () => {
    try {
      if (!form.role_code.trim()) {
        showSnackbar("Role code is required", "error");
        return;
      }

      if (!form.role_name.trim()) {
        showSnackbar("Role name is required", "error");
        return;
      }

      const payload = {
        ...form,
        client_id: clientId,
        college_id: collegeId,
      };

      if (editId) {
        await axios.put(
          `${API_BASE_URL}/api/rbacall/roles/${clientId}/${collegeId}/${editId}`,
          payload
        );

        showSnackbar("Role updated successfully");
      } else {
        await axios.post(`${API_BASE_URL}/api/rbacall/roles/${clientId}/${collegeId}`, payload);

        showSnackbar("Role created successfully");
      }

      setOpenDialog(false);

      loadRoles();
    } catch (error) {
      console.error(error);

      showSnackbar(error.response?.data?.message || "Failed to save role", "error");
    }
  };

  // ---------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Delete Role?",
      text: `Are you sure you want to delete "${row.role_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/rbacall/roles/${clientId}/${collegeId}/${row.role_id}`
      );

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Role deleted successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      loadRoles();
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete role",
      });
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      sortable: false,
      renderCell: (params) => page * pageSize + params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "role_id",
      headerName: "Role No",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "role_code",
      headerName: "Role Code",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "role_name",
      headerName: "Role Name",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "role_description",
      headerName: "Description",
      flex: 2,
      minWidth: 220,
    },
    {
      field: "is_system_role",
      headerName: "System",
      width: 100,
      renderCell: (params) =>
        params.value ? <Typography color="primary">Yes</Typography> : <Typography>No</Typography>,
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Typography color="success.main">Active</Typography>
        ) : (
          <Typography color="error.main">Inactive</Typography>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu
          onEdit={() => handleEdit(params.row)}
          onDelete={() => handleDelete(params.row)}
        />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={2}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5">ROLES </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ color: "#fff" }}
            >
              Add Role
            </Button>
          </Stack>
        </Toolbar>

        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Role" : "Add Role"}</DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Role Code"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.role_code}
              onChange={(e) =>
                setForm({
                  ...form,
                  role_code: e.target.value.toUpperCase(),
                })
              }
              required
            />

            <TextField
              label="Role Name"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.role_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  role_name: e.target.value,
                })
              }
              required
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={3}
              InputLabelProps={{ shrink: true }}
              value={form.role_description}
              onChange={(e) =>
                setForm({
                  ...form,
                  role_description: e.target.value,
                })
              }
            />

            <TextField
              select
              label="System Role"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.is_system_role}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_system_role: Number(e.target.value),
                })
              }
              SelectProps={{ native: true }}
            >
              <option value={0}>No</option>
              <option value={1}>Yes</option>
            </TextField>

            <TextField
              select
              label="Status"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.is_active}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: Number(e.target.value),
                })
              }
              SelectProps={{ native: true }}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>

          <Button variant="contained" onClick={handleSave} sx={{ color: "#fff" }}>
            {editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Roles;
